import type React from "react";
import { motion } from "framer-motion";
import { useState } from "react";
import { WalletTransaction, WalletTransactionStatus, WalletTransactionType } from "../../../types/stores";
import OrderDetailsModal from "../order/OrderDetailsModal";

// TransactionRow المعدل
export const TransactionRow: React.FC<{
  transaction: WalletTransaction;
  getTransactionTypeText: (type: WalletTransactionType) => string;
  getStatusColor: (status: WalletTransactionStatus) => string;
}> = ({ transaction, getTransactionTypeText, getStatusColor }) => {
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const isPositive = transaction.type === 'collect' || transaction.type === 'refund';
  
  // دالة لاستخراج order_id من transaction
  const extractOrderId = (): number | null => {
    // الطريقة 1: إذا كان هناك حقل order_id مباشرةً
    if (transaction.order_id) {
      return transaction.order_id;
    }
    
    // الطريقة 2: البحث عن order ID في note
    if (transaction.note) {
      // محاولة استخراج رقم من النص
      const orderIdMatch = transaction.note.match(/Order #?(\d+)/i) || 
                         transaction.note.match(/order[ :]?(\d+)/i) ||
                         transaction.note.match(/(\d+)/);
      
      if (orderIdMatch && orderIdMatch[1]) {
        return parseInt(orderIdMatch[1]);
      }
    }
    
    // الطريقة 3: إذا كان transaction.type يشير إلى طلب
    if (transaction.type === 'refund' || transaction.type === 'withdraw') {
      // يمكنك تعديل هذا بناءً على منطق التطبيق الخاص بك
      return transaction.id; // أو أي منطق آخر
    }
    
    return null;
  };
  
  const handleRowClick = () => {
    const orderId = extractOrderId();
    if (orderId) {
      setSelectedOrderId(orderId);
      setIsModalOpen(true);
    } else {
      // إذا لم يتم العثور على order ID، يمكنك عرض رسالة أو القيام بشيء آخر
      // console.log("No order ID found for this transaction");
      // يمكنك هنا عرض رسالة للمستخدم أو عدم فتح المودال
    }
  };
  
  return (
    <>
      <motion.tr
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
        onClick={handleRowClick}
      >
        <td className="py-4 px-4">
          <div className="text-sm font-medium text-gray-900">
            {new Date(transaction.created_at).toLocaleDateString()}
          </div>
          <div className="text-xs text-gray-500">
            {new Date(transaction.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </td>
        <td className="py-4 px-4">
          <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${
            isPositive 
              ? 'bg-green-100 text-green-800 border border-green-200' 
              : 'bg-blue-100 text-blue-800 border border-blue-200'
          }`}>
            {getTransactionTypeText(transaction.type)}
          </span>
        </td>
        <td className="py-4 px-4">
          <div className="max-w-xs">
            <p className="text-sm font-medium text-gray-900 truncate">
              {transaction.note || getTransactionTypeText(transaction.type)}
            </p>
          </div>
        </td>
        <td className="py-4 px-4">
          <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}>
            {transaction.status}
          </span>
        </td>
        <td className="py-4 px-4">
          <span className={`text-sm font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {isPositive ? '+' : '-'}${parseFloat(transaction.amount).toFixed(2)}
          </span>
        </td>
        <td className="py-4 px-4">
          <span className="text-sm text-gray-600">
            ${parseFloat(transaction.fee || '0').toFixed(2)}
          </span>
        </td>
      </motion.tr>
      
      {/* Modal لتوضيح تفاصيل الطلب */}
      <OrderDetailsModal
        orderId={selectedOrderId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};